import React, { useState, useEffect, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, doc, setDoc, deleteDoc, collection, query, onSnapshot, orderBy, limit, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Clock, TrendingUp, Link as LinkIcon, MessageSquare, Globe, Bot, Server as ServerIcon, MoreVertical, Zap, ShieldOff, Wrench, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import StatusMarkdown from './StatusMarkdown';
import { UPTIME_WINDOWS, uptimePercent, averageUptime, formatUptimePct, formatDuration, toMillis } from './uptime';
import { mergeSplitOutages } from './coalesceIncidents';

const AdminStatusTools = React.lazy(() => import('./AdminStatusTools'));

// --- Configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyD3bmeQTw3KluPviZ1msricT9q9hwcoC80",
  authDomain: "frogmaster-status-page.firebaseapp.com",
  projectId: "frogmaster-status-page",
  storageBucket: "frogmaster-status-page.firebasestorage.app",
  messagingSenderId: "921262465948",
  appId: "1:921262465948:web:3c9626a5b85df60c065e59",
  measurementId: "G-Y3VFW5J42G"
};

const ADMIN_EMAIL = "1frogmaster@gmail.com";

const FrogIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" {...props}>
    <g><path d="M472.881,141.926c-5.347-3.038-12.149-1.175-15.188,4.179c-1.017,1.784-1.476,3.734-1.447,5.655 c-7.39,3.455-14.643,4.716-22.585,5.977c-7.238,2.33-16.871,4.96-2.608-43.577c0.193,0.008,0.387,0.029,0.581,0.029 c6.945,0,12.578-5.634,12.578-12.586c0-6.952-5.633-12.586-12.578-12.586c-6.945,0-12.579,5.634-12.579,12.586 c0,3.914,1.785,7.404,4.58,9.712c-3.741,12.736-9.074,28.791-15.818,36.79c-5.096,7.884-16.556,4.73-26.805-5.547 c0.415-1.29,0.645-2.652,0.645-4.078c0-7.31-5.927-13.23-13.231-13.23c-7.31,0-13.238,5.92-13.238,13.23 c0,7.304,5.928,13.231,13.238,13.231c2.802,0,5.404-0.882,7.547-2.372c10.221,7.841,18.807,18.434,12.722,35.815 c-4.057,15.202-13.912,50.551-17.796,53.153c-2.609,1.755-20.893-12.271-35.149-23.652c0.122-5.01,1.204-7.813,2.358-9.282 c18.14-23.114,3.397-49.526-13.066-56.693c-22.985-10.012-17.932-28.719-42.681-32.016c-24.749-3.289-25.315,13.367-49.562,19.739 c-31.938,8.392-32.554,39.097-27.436,51.303c0.58,1.383,1.075,3.447,0.444,6.831c-14.972-10.364-58.169-36.74-56.628-40.638 c1.728-4.344,25.487-68.298,35.284-73.794c8.243-4.623,14.75-2.265,25.308-0.91c1.591,5.526,6.673,9.568,12.707,9.568 c7.311,0,13.231-5.927,13.231-13.238c0-7.304-5.92-13.231-13.231-13.231c-5.196,0-9.683,3.003-11.847,7.361 c-12.88-3.154-23.595-8.357-19.431-16.134c3.835-7.174,12.242-19.387,16.958-27.071c7.189-0.136,12.973-5.992,12.973-13.216 C221.13,5.927,215.203,0,207.892,0c-7.304,0-13.231,5.927-13.231,13.231c0,3.025,1.025,5.806,2.738,8.042 c-17.961,26.762-28.103,30.296-35.248,19.71c-5.369-6.759-9.153-10.558-12.615-16.693c1.405-1.863,2.251-4.178,2.251-6.687 c0-6.164-4.989-11.153-11.146-11.153c-6.156,0-11.145,4.989-11.145,11.153c0,6.15,4.989,11.138,11.145,11.138 c0.258,0,0.516-0.014,0.767-0.035c0.28,0.522,0.595,1.053,0.946,1.591c5.641,8.686,15.962,29.292,13.597,42.609 c-4.522,25.472-38.352,68.777-33.694,85.427c6.816,24.376,60.836,74.059,69.207,81.642c-6.357,20.714-12.17,45.068-11.317,68.404 c0.33,9.189,1.713,17.99,3.885,26.283c-22.742,3.634-77.206,13.28-87.591,23.609c-13.196,13.13,20.254,80.517,25.45,92.687 c8.221,19.258,8.221,26.626,8.221,26.626s-2.767,19.344-23.072-18.298c-4.1-13.252-10.872-25.193-15.854-37.363 c2.358-2.272,3.835-5.461,3.835-8.988c0-6.909-5.605-12.514-12.507-12.514c-6.909,0-12.514,5.605-12.514,12.514 c0,6.328,4.709,11.546,10.816,12.378c3.948,12.45,10.149,27.501,10.328,33.006c0.229,6.894-4.573,8.55-10.723,3.154 c-7.755-6.81-15.926-15.653-21.745-21.638c0.071-0.344,0.143-0.688,0.193-1.046c0.91-6.845-3.906-13.138-10.75-14.041 c-6.845-0.917-13.138,3.899-14.048,10.743c-0.91,6.846,3.899,13.138,10.751,14.048c1.411,0.194,2.802,0.122,4.128-0.15 c8.866,11.496,33.313,32.023,24.275,31.729c-6.408-0.208-11.589,0.079-15.725,0.688c-1.899-4.48-6.343-7.626-11.517-7.626 c-6.909,0-12.507,5.605-12.507,12.514c0,6.902,5.598,12.507,12.507,12.507c4.458,0,8.364-2.344,10.579-5.855 c13.352,0.208,25.436,0.551,30.246,2.472c63.574,26.906,83.699,19.997,76.26-18.772c-7.433-38.775-16.872-86.888-16.872-86.888 l60.843-9.684c12.055,14.901,25.96,24.24,36.804,25.681v0.007h0.007c3.913,0.517,8.479,0.136,13.446-1.074l0.128-0.015l0.201-0.072 c4.429-1.103,9.159-2.867,14.041-5.267l24.841-9.325c-1.871,1.527-3.784,3.254-5.726,5.21c-3.233,3.254-6.537,7.146-9.848,11.79 c-22.943,32.131,22.226,47.282,59.187,46.766c5.204-0.071,9.332,1.068,12.801,2.774c0,0,0,0,0.007,0 c4.05,1.985,7.204,4.751,10.106,7.275c-0.014,0.064-0.028,0.129-0.036,0.193c-0.91,6.845,3.899,13.131,10.744,14.041 c6.852,0.911,13.137-3.898,14.054-10.75c0.91-6.845-3.906-13.138-10.75-14.048c-1.993-0.265-3.928-0.028-5.706,0.581 c-2.272-2.917-4.981-6.272-8.12-10.113c-5.254-6.437,14.349-4.924,27.494-9.805c2.014,1.376,4.443,2.193,7.067,2.193 c6.916,0,12.514-5.605,12.514-12.514c0-6.909-5.598-12.507-12.514-12.507c-5.97,0-10.952,4.186-12.199,9.783 c-7.755,2.623-17.366,5.397-24.483,4.501c-8.12-1.025-6.981-7.547-6.981-7.547c2.165-4.343,10.601-10.916,18.198-18.126 c1.334,0.488,2.76,0.76,4.257,0.76c6.909,0,12.507-5.598,12.507-12.507c0-6.903-5.598-12.507-12.507-12.507 c-6.909,0-12.507,5.604-12.507,12.507c0,1.634,0.323,3.189,0.889,4.622c-8.407,8.959-17.638,17.948-31.135,22.692 c-9.589,4.172-7.891,2.172-7.891,2.172s-6.701-0.172,3.14-9.024c9.848-8.844,71.937-58.005,61.796-73.622 c-8.673-13.367-36.346-12.55-60.52-5.124c0.179-0.552,0.373-1.082,0.544-1.634c6.422-20.692,7.533-43.714,7.096-64.219 c14.872,8.078,29.4,14.464,38.954,15.023c17.259,1.003,22.491-65.703,34.288-88.724c6.171-12.041,37.363-26.333,47.11-29.83 c0.595-0.215,1.161-0.452,1.692-0.696c0.559,0.487,1.168,0.925,1.835,1.305c5.354,3.046,12.156,1.175,15.202-4.179 C480.106,151.774,478.235,144.966,472.881,141.926z" /></g></svg>
);

const servicesConfig = [
  // --- NEW: 'canRestart' flag to control which services show the restart button ---
  { id: 'hbtranslate', name: 'HephBot Translate Solutions Webpage', url: 'https://hbtranslate.solutions', link: 'https://hbtranslate.solutions', icon: <Globe size={20} />, canRestart: true },
  { id: 'hbt', name: 'HephBot Translate Discord Bot', url: 'https://api.hbtranslate.solutions', link: 'https://hbtranslate.solutions', icon: <Bot size={20} />, canRestart: true },
  { id: 'hbtdocs', name: 'HephBot Translate Documentation', url: 'https://docs.hbtranslate.solutions', link: 'https://docs.hbtranslate.solutions', icon: <Globe size={20} />, canRestart: true },
  { id: 'wfinfo', name: 'WFInfo Bot', url: 'https://wfinfo.frogmaster.xyz/ping', link: 'https://wfinfo.warframestat.us/', icon: <Bot size={20} />, canRestart: true },
  { id: 'search', name: 'Free Search', url: 'https://search.frogmaster.xyz', link: 'https://search.frogmaster.xyz', icon: <Globe size={20} />, canRestart: true },
  { id: 'files', name: 'File Server', url: 'https://nextcloud.frogmaster.xyz', link: 'https://nextcloud.frogmaster.xyz', icon: <ServerIcon size={20} /> },
  { id: 'domain', name: 'Caddy Server', url: 'https://pihole.frogmaster.xyz', link: 'https://caddyserver.com/docs/', icon: <ServerIcon size={20} /> },
];

const STATUS_STYLES = {
  OPERATIONAL: { color: 'text-green-400', bgColor: 'bg-green-400/10', borderColor: 'border-green-400/20', shadow: 'shadow-green-400/20', text: 'Operational', stroke: '#22c55e' },
  DEGRADED:    { color: 'text-yellow-400', bgColor: 'bg-yellow-400/10', borderColor: 'border-yellow-400/20', shadow: 'shadow-yellow-400/20', text: 'Degraded', stroke: '#eab308' },
  DETECTED:    { color: 'text-orange-400', bgColor: 'bg-orange-400/10', borderColor: 'border-orange-400/20', shadow: 'shadow-orange-400/20', text: 'Issue Detected', stroke: '#f97316' },
  DOWN:        { color: 'text-red-500', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/20', shadow: 'shadow-red-500/20', text: 'Service Down', stroke: '#ef4444' },
  MAINTENANCE: { color: 'text-blue-400', bgColor: 'bg-blue-400/10', borderColor: 'border-blue-400/20', shadow: 'shadow-blue-400/20', text: 'Maintenance', stroke: '#3b82f6' },
  DISABLED:    { color: 'text-neutral-500', bgColor: 'bg-neutral-500/10', borderColor: 'border-neutral-700', shadow: '', text: 'Disabled', stroke: '#737373' },
  UPDATE:      { color: 'text-blue-400', bgColor: 'bg-blue-400/10', borderColor: 'border-blue-400/20', shadow: 'shadow-blue-400/20', text: 'Update', stroke: '#3b82f6' },
  UNKNOWN:     { color: 'text-neutral-500', bgColor: 'bg-neutral-500/10', borderColor: 'border-neutral-700', shadow: '', text: 'No Data', stroke: '#737373' },
};

const PUBLIC_HEADLINES = {
  OPERATIONAL: 'All systems up',
  DEGRADED: 'Some systems have an issue',
  DETECTED: 'Some systems have an issue',
  DOWN: 'Some systems are down',
  MAINTENANCE: 'Maintenance in progress',
  UPDATE: 'Updates in progress',
  UNKNOWN: 'Checking system status',
};

const PUBLIC_LABELS = {
  OPERATIONAL: 'Up',
  DEGRADED: 'Issue',
  DETECTED: 'Issue',
  DOWN: 'Down',
  MAINTENANCE: 'Maintenance',
  UPDATE: 'Update',
  DISABLED: 'Paused',
  UNKNOWN: 'Checking',
};

function windowPhrase(windowId) {
  const phrases = { h24: '24-hour', d7: '7-day', d30: '30-day', d90: '90-day', all: 'All-time' };
  return phrases[windowId] || '90-day';
}

function statusLabel(statusKey, isAdmin) {
  if (isAdmin) return (STATUS_STYLES[statusKey] || STATUS_STYLES.UNKNOWN).text;
  return PUBLIC_LABELS[statusKey] || (STATUS_STYLES[statusKey] || STATUS_STYLES.UNKNOWN).text;
}

// Status comes from the Google probe. Latency is never a failure.
// Maintenance / disabled flags from Travis always win.
function effectiveStatus(liveData) {
  if (!liveData) return 'UNKNOWN';
  const raw = liveData.status;
  if (raw === 'MAINTENANCE') return 'MAINTENANCE';
  if (raw === 'DISABLED' || liveData.monitoring === 'disabled') return 'DISABLED';
  if (raw === 'DETECTED') return 'DEGRADED';
  return raw || 'UNKNOWN';
}

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

export default function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [userServiceIds, setUserServiceIds] = useState([]);
  const [services, setServices] = useState([]);
  const [history, setHistory] = useState({});
  const [incidents, setIncidents] = useState([]);
  const [uptimeWindow, setUptimeWindow] = useState('d90');
  const [isIncidentModalOpen, setIncidentModalOpen] = useState(false);
  const [selectedServiceForIncident, setSelectedServiceForIncident] = useState(null);
  const [notification, setNotification] = useState(null);
  
  // User management state
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false);
  const [users, setUsers] = useState([]);

  const HISTORY_LISTEN_DAYS = 30;

  // User management functions
  const functions = getFunctions(app);
  
  const loadUsers = async () => {
    try {
      const manageUser = httpsCallable(functions, 'manageUser');
      const result = await manageUser({ action: 'list' });
      if (result.data.success) {
        setUsers(result.data.users);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setNotification({ type: 'error', message: 'Failed to load users' });
    }
  };

  const createUser = async (email, permissions, serviceIds) => {
    try {
      const manageUser = httpsCallable(functions, 'manageUser');
      const result = await manageUser({
        action: 'create',
        targetEmail: email,
        permissions: permissions,
        serviceIds: serviceIds
      });
      
      if (result.data.success) {
        setNotification({ type: 'success', message: result.data.message });
        loadUsers();
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setNotification({ type: 'error', message: 'Failed to create user' });
    }
  };

  const updateUser = async (userEmail, permissions, serviceIds) => {
    try {
      const manageUser = httpsCallable(functions, 'manageUser');
      const result = await manageUser({
        action: 'update',
        targetEmail: userEmail,
        permissions: permissions,
        serviceIds: serviceIds
      });
      
      if (result.data.success) {
        setNotification({ type: 'success', message: result.data.message });
        loadUsers();
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setNotification({ type: 'error', message: 'Failed to update user' });
    }
  };

  const deleteUser = async (userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user ${userEmail}?`)) return;
    
    try {
      const manageUser = httpsCallable(functions, 'manageUser');
      const result = await manageUser({
        action: 'delete',
        targetEmail: userEmail
      });
      
      if (result.data.success) {
        setNotification({ type: 'success', message: result.data.message });
        loadUsers();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setNotification({ type: 'error', message: 'Failed to delete user' });
    }
  };

  const cleanupOldUser = async (userId) => {
    if (!window.confirm(`Are you sure you want to cleanup old user document ${userId}?`)) return;
    
    try {
      // Delete directly from Firestore since this is an old user document
      await deleteDoc(doc(db, 'users', userId));
      setNotification({ type: 'success', message: 'Old user document deleted successfully' });
      loadUsers();
    } catch (error) {
      console.error('Error cleaning up old user:', error);
      setNotification({ type: 'error', message: `Failed to cleanup old user: ${error.message}` });
    }
  };

  useEffect(() => {
    document.title = "Frogmaster's Status Page";
    const favicon = document.querySelector("link[rel*='icon']");
    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="%2322c55e"><g><path d="M472.881,141.926c-5.347-3.038-12.149-1.175-15.188,4.179c-1.017,1.784-1.476,3.734-1.447,5.655 c-7.39,3.455-14.643,4.716-22.585,5.977c-7.238,2.33-16.871,4.96-2.608-43.577c0.193,0.008,0.387,0.029,0.581,0.029 c6.945,0,12.578-5.634,12.578-12.586c0-6.952-5.633-12.586-12.578-12.586c-6.945,0-12.579,5.634-12.579,12.586 c0,3.914,1.785,7.404,4.58,9.712c-3.741,12.736-9.074,28.791-15.818,36.79c-5.096,7.884-16.556,4.73-26.805-5.547 c0.415-1.29,0.645-2.652,0.645-4.078c0-7.31-5.927-13.23-13.231-13.23c-7.31,0-13.238,5.92-13.238,13.23 c0,7.304,5.928,13.231,13.238,13.231c2.802,0,5.404-0.882,7.547-2.372c10.221,7.841,18.807,18.434,12.722,35.815 c-4.057,15.202-13.912,50.551-17.796,53.153c-2.609,1.755-20.893-12.271-35.149-23.652c0.122-5.01,1.204-7.813,2.358-9.282 c18.14-23.114,3.397-49.526-13.066-56.693c-22.985-10.012-17.932-28.719-42.681-32.016c-24.749-3.289-25.315,13.367-49.562,19.739 c-31.938,8.392-32.554,39.097-27.436,51.303c0.58,1.383,1.075,3.447,0.444,6.831c-14.972-10.364-58.169-36.74-56.628-40.638 c1.728-4.344,25.487-68.298,35.284-73.794c8.243-4.623,14.75-2.265,25.308-0.91c1.591,5.526,6.673,9.568,12.707,9.568 c7.311,0,13.231-5.927,13.231-13.238c0-7.304-5.92-13.231-13.231-13.231c-5.196,0-9.683,3.003-11.847,7.361 c-12.88-3.154-23.595-8.357-19.431-16.134c3.835-7.174,12.242-19.387,16.958-27.071c7.189-0.136,12.973-5.992,12.973-13.216 C221.13,5.927,215.203,0,207.892,0c-7.304,0-13.231,5.927-13.231,13.231c0,3.025,1.025,5.806,2.738,8.042 c-17.961,26.762-28.103,30.296-35.248,19.71c-5.369-6.759-9.153-10.558-12.615-16.693c1.405-1.863,2.251-4.178,2.251-6.687 c0-6.164-4.989-11.153-11.146-11.153c-6.156,0-11.145,4.989-11.145,11.153c0,6.15,4.989,11.138,11.145,11.138 c0.258,0,0.516-0.014,0.767-0.035c0.28,0.522,0.595,1.053,0.946,1.591c5.641,8.686,15.962,29.292,13.597,42.609 c-4.522,25.472-38.352,68.777-33.694,85.427c6.816,24.376,60.836,74.059,69.207,81.642c-6.357,20.714-12.17,45.068-11.317,68.404 c0.33,9.189,1.713,17.99,3.885,26.283c-22.742,3.634-77.206,13.28-87.591,23.609c-13.196,13.13,20.254,80.517,25.45,92.687 c8.221,19.258,8.221,26.626,8.221,26.626s-2.767,19.344-23.072-18.298c-4.1-13.252-10.872-25.193-15.854-37.363 c2.358-2.272,3.835-5.461,3.835-8.988c0-6.909-5.605-12.514-12.507-12.514c-6.909,0-12.514,5.605-12.514,12.514 c0,6.328,4.709,11.546,10.816,12.378c3.948,12.45,10.149,27.501,10.328,33.006c0.229,6.894-4.573,8.55-10.723,3.154 c-7.755-6.81-15.926-15.653-21.745-21.638c0.071-0.344,0.143-0.688,0.193-1.046c0.91-6.845-3.906-13.138-10.75-14.041 c-6.845-0.917-13.138,3.899-14.048,10.743c-0.91,6.846,3.899,13.138,10.751,14.048c1.411,0.194,2.802,0.122,4.128-0.15 c8.866,11.496,33.313,32.023,24.275,31.729c-6.408-0.208-11.589,0.079-15.725,0.688c-1.899-4.48-6.343-7.626-11.517-7.626 c-6.909,0-12.507,5.605-12.507,12.514c0,6.902,5.598,12.507,12.507,12.507c4.458,0,8.364-2.344,10.579-5.855 c13.352,0.208,25.436,0.551,30.246,2.472c63.574,26.906,83.699,19.997,76.26-18.772c-7.433-38.775-16.872-86.888-16.872-86.888 l60.843-9.684c12.055,14.901,25.96,24.24,36.804,25.681v0.007h0.007c3.913,0.517,8.479,0.136,13.446-1.074l0.128-0.015l0.201-0.072 c4.429-1.103,9.159-2.867,14.041-5.267l24.841-9.325c-1.871,1.527-3.784,3.254-5.726,5.21c-3.233,3.254-6.537,7.146-9.848,11.79 c-22.943,32.131,22.226,47.282,59.187,46.766c5.204-0.071,9.332,1.068,12.801,2.774c0,0,0,0,0.007,0 c4.05,1.985,7.204,4.751,10.106,7.275c-0.014,0.064-0.028,0.129-0.036,0.193c-0.91,6.845,3.899,13.131,10.744,14.041 c6.852,0.911,13.137-3.898,14.054-10.75c0.91-6.845-3.906-13.138-10.75-14.048c-1.993-0.265-3.928-0.028-5.706,0.581 c-2.272-2.917-4.981-6.272-8.12-10.113c-5.254-6.437,14.349-4.924,27.494-9.805c2.014,1.376,4.443,2.193,7.067,2.193 c6.916,0,12.514-5.605,12.514-12.514c0-6.909-5.598-12.507-12.514-12.507c-5.97,0-10.952,4.186-12.199,9.783 c-7.755,2.623-17.366,5.397-24.483,4.501c-8.12-1.025-6.981-7.547-6.981-7.547c2.165-4.343,10.601-10.916,18.198-18.126 c1.334,0.488,2.76,0.76,4.257,0.76c6.909,0,12.507-5.598,12.507-12.507c0-6.903-5.598-12.507-12.507-12.507 c-6.909,0-12.507,5.604-12.507,12.507c0,1.634,0.323,3.189,0.889,4.622c-8.407,8.959-17.638,17.948-31.135,22.692 c-9.589,4.172-7.891,2.172-7.891,2.172s-6.701-0.172,3.14-9.024c9.848-8.844,71.937-58.005,61.796-73.622 c-8.673-13.367-36.346-12.55-60.52-5.124c0.179-0.552,0.373-1.082,0.544-1.634c6.422-20.692,7.533-43.714,7.096-64.219 c14.872,8.078,29.4,14.464,38.954,15.023c17.259,1.003,22.491-65.703,34.288-88.724c6.171-12.041,37.363-26.333,47.11-29.83 c0.595-0.215,1.161-0.452,1.692-0.696c0.559,0.487,1.168,0.925,1.835,1.305c5.354,3.046,12.156,1.175,15.202-4.179 C480.106,151.774,478.235,144.966,472.881,141.926z" /></g></svg>`;
    favicon.href = `data:image/svg+xml,${svgString}`;

    const unsubAuth = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setIsAdmin(currentUser?.email === ADMIN_EMAIL);
      if (currentUser) {
        console.log('User authenticated:', currentUser.email);
        
        // First, try to find existing user by email (to preserve permissions)
        const userDocRef = doc(db, 'users', currentUser.email);
        
        try {
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists) {
            // Found existing user by email - use their document
            const userData = userDoc.data();
            console.log('User document found:', userData);
            console.log('User permissions:', userData.permissions);
            console.log('User service IDs:', userData.serviceIds);
            setUserPermissions(userData.permissions || []);
            setUserServiceIds(userData.serviceIds || []);
          } else {
            // No existing user found - create new one
            console.log('No user document found, creating new one...');
            setUserPermissions([]);
            setUserServiceIds([]);
            // Try to create user document automatically
            try {
              await setDoc(userDocRef, {
                email: currentUser.email,
                uid: currentUser.uid,
                permissions: [],
                serviceIds: [],
                createdAt: new Date(),
                isActive: true
              });
              console.log('Created new user document');
            } catch (error) {
              console.error('Failed to create user document:', error);
            }
          }
        } catch (error) {
          console.error('Error fetching user document:', error);
          console.error('Error details:', error.code, error.message);
        }
      } else {
        setUserPermissions([]);
        setUserServiceIds([]);
      }
    });

    const qStatus = query(collection(db, 'status'));
    const unsubStatus = onSnapshot(qStatus, (snapshot) => {
        const statuses = snapshot.docs.map(doc => doc.data());
        const serviceData = servicesConfig.map(config => {
            const liveData = statuses.find(s => s.id === config.id);
            const merged = { ...config, ...liveData };
            merged.status = effectiveStatus(merged);
            return merged;
        });
        setServices(serviceData);
    });
    const today = new Date();
    const unsubHistory = [];
    for (let i = 0; i < HISTORY_LISTEN_DAYS; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateString = date.toISOString().split('T')[0];
      const qHistory = query(collection(db, `history/${dateString}/events`), orderBy('timestamp', 'desc'));
      const unsub = onSnapshot(qHistory, (snapshot) => {
        const events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setHistory(prev => ({ ...prev, [dateString]: { events } }));
      });
      unsubHistory.push(unsub);
    }
    const unsubIncidents = onSnapshot(collection(db, 'incidents'), (snapshot) => {
      setIncidents(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => { 
        unsubAuth(); 
        unsubStatus(); 
        unsubHistory.forEach(unsub => unsub());
        unsubIncidents();
    };
  }, []);

  useEffect(() => {
    if (isUserManagementOpen) {
      loadUsers();
    }
    // loadUsers is recreated each render; only refetch when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserManagementOpen]);

  const { serviceUptimes, overallUptime } = useMemo(() => {
    const now = Date.now();
    const selected = UPTIME_WINDOWS.find((w) => w.id === uptimeWindow) || UPTIME_WINDOWS[3];
    const uptimes = {};
    servicesConfig.forEach((service) => {
      const live = services.find((s) => s.id === service.id);
      const monitoredSince = toMillis(live?.monitoredSince) || 0;
      const svcIncidents = mergeSplitOutages(
        incidents.filter((i) => i.serviceId === service.id)
      );
      uptimes[service.id] = uptimePercent(svcIncidents, selected.ms, now, monitoredSince);
    });
    const monitored = services.filter((s) => s.status !== 'DISABLED' && s.monitoring !== 'disabled');
    const ids = monitored.length ? monitored.map((s) => s.id) : servicesConfig.map((s) => s.id);
    const overall = averageUptime(ids.map((id) => uptimes[id]));
    return { serviceUptimes: uptimes, overallUptime: overall };
  }, [incidents, services, uptimeWindow]);

  const overallStatusKey = useMemo(() => {
    const rank = { DOWN: 5, DETECTED: 4, DEGRADED: 3, MAINTENANCE: 2, UPDATE: 1, OPERATIONAL: 0, UNKNOWN: 0, DISABLED: -1 };
    let worst = 'OPERATIONAL';
    let worstRank = -1;
    services.forEach((service) => {
      if (!service.status || service.status === 'DISABLED') return;
      const nextRank = rank[service.status] ?? 0;
      if (nextRank > worstRank) {
        worstRank = nextRank;
        worst = service.status;
      }
    });
    if (services.length === 0) return 'UNKNOWN';
    return worstRank < 0 ? 'OPERATIONAL' : worst;
  }, [services]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleSetStatus = async (serviceId, newStatus) => {
    const serviceRef = doc(db, 'status', serviceId);
    let payload = { status: newStatus, monitoring: 'enabled' };
    if (newStatus === 'DISABLED') {
        payload = { status: 'DISABLED', monitoring: 'disabled' };
    }
    await setDoc(serviceRef, payload, { merge: true });
    showNotification(`Service status set to ${newStatus.toLowerCase()}.`);
  };

  const handleRestart = async (service) => {
    if (!window.confirm(`Are you sure you want to restart ${service.name}?`)) return;
    
    // --- NEW: Call the secure Cloud Function ---
    try {
        const functions = getFunctions(app);
        const restartService = httpsCallable(functions, 'restartService');
        const result = await restartService({ serviceId: service.id, serviceName: service.name });
        showNotification(result.data.message);
    } catch (error) {
        console.error("Restart error:", error);
        showNotification(`Failed to send restart command: ${error.message}`, 'error');
    }
  };
  
  const handleDeleteIncident = async (dateString, eventId, force = false) => {
      if (!force && !window.confirm("Are you sure you want to delete this incident log?")) return;
      try {
          const incidentRef = doc(db, `history/${dateString}/events`, eventId);
          await deleteDoc(incidentRef);
          showNotification("Incident deleted successfully.");
      } catch (error) {
          console.error("Error deleting incident:", error);
          showNotification("Failed to delete incident.", "error");
      }
  };

  const openIncidentModal = (service) => {
    setSelectedServiceForIncident(service);
    setIncidentModalOpen(true);
  };

  return (
    <>
      <div className="grid-bg" aria-hidden="true"></div>
      <div className="bg-neutral-950 text-neutral-200 min-h-screen font-sans antialiased relative z-10">
        <Header user={user} onLogin={() => signInWithPopup(auth, new GoogleAuthProvider())} onLogout={() => signOut(auth)} overallUptime={overallUptime} overallStatusKey={overallStatusKey} isAdmin={isAdmin} onUserManagement={() => setIsUserManagementOpen(true)} uptimeWindow={uptimeWindow} />
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <OverallBanner statusKey={overallStatusKey} overallUptime={overallUptime} serviceCount={services.length} uptimeWindow={uptimeWindow} onUptimeWindow={setUptimeWindow} isAdmin={isAdmin} />
          <ServiceGrid services={services} isAdmin={isAdmin} userPermissions={userPermissions} userServiceIds={userServiceIds} onAddIncident={openIncidentModal} onSetStatus={handleSetStatus} onRestart={handleRestart} serviceUptimes={serviceUptimes} uptimeWindow={uptimeWindow} />
          <HistoryLog history={history} incidents={incidents} isAdmin={isAdmin} userPermissions={userPermissions} userServiceIds={userServiceIds} onDeleteIncident={handleDeleteIncident} />
        </main>
        <Footer />
        {isIncidentModalOpen && <IncidentModal service={selectedServiceForIncident} onClose={() => setIncidentModalOpen(false)} />}
        {notification && <Notification message={notification.message} type={notification.type} onDismiss={() => setNotification(null)} />}
        {isUserManagementOpen && (
          <UserManagementModal
            isOpen={isUserManagementOpen}
            onClose={() => setIsUserManagementOpen(false)}
            users={users}
            onCreateUser={createUser}
            onUpdateUser={updateUser}
            onDeleteUser={deleteUser}
            onCleanupOldUser={cleanupOldUser}
            servicesConfig={servicesConfig}
          />
        )}
      </div>
    </>
  );
}

// --- Components ---

const OverallBanner = ({ statusKey, overallUptime, serviceCount, uptimeWindow, onUptimeWindow, isAdmin }) => {
  const style = STATUS_STYLES[statusKey] || STATUS_STYLES.UNKNOWN;
  const selected = UPTIME_WINDOWS.find((w) => w.id === uptimeWindow) || UPTIME_WINDOWS[3];
  return (
    <section className={`mb-8 md:mb-10 rounded-2xl border ${style.borderColor} ${style.bgColor} px-5 py-4 md:px-6 md:py-5 flex flex-col gap-4 shadow-card`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={`inline-flex h-2.5 w-2.5 rounded-full bg-current ${style.color} ${statusKey === 'OPERATIONAL' ? 'animate-pulse' : ''}`}></span>
          <h2 className={`text-lg md:text-xl font-semibold tracking-tight ${style.color}`}>{PUBLIC_HEADLINES[statusKey] || style.text}</h2>
        </div>
        <p className="text-sm text-neutral-400">
          {windowPhrase(selected.id)} uptime <span className="ml-1.5 font-semibold text-white tabular-nums">{formatUptimePct(overallUptime)}</span>
        </p>
      </div>
      {isAdmin && (
        <React.Suspense fallback={null}>
          <AdminStatusTools uptimeWindow={uptimeWindow} onUptimeWindow={onUptimeWindow} serviceCount={serviceCount} />
        </React.Suspense>
      )}
    </section>
  );
};

const Header = ({ user, onLogin, onLogout, overallUptime, overallStatusKey, isAdmin, onUserManagement, uptimeWindow }) => {
  const style = STATUS_STYLES[overallStatusKey] || STATUS_STYLES.UNKNOWN;
  const selected = UPTIME_WINDOWS.find((w) => w.id === uptimeWindow) || UPTIME_WINDOWS[3];
  return (
    <header className="bg-neutral-950/75 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center gap-4 min-h-[4.25rem] py-3">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-400/10 ring-1 ring-green-400/20">
              <FrogIcon className="w-6 h-6 text-green-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-white tracking-tight truncate">Frogmaster Status</h1>
              <p className="text-xs sm:text-sm text-neutral-400">Live service health</p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <span className={`hidden md:inline-flex items-center gap-2 rounded-full border ${style.borderColor} ${style.bgColor} px-3 py-1 text-xs font-medium ${style.color}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-current"></span>
              {formatUptimePct(overallUptime)} {windowPhrase(selected.id)}
            </span>
            {isAdmin && (
              <button
                onClick={onUserManagement}
                className="px-3 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors text-sm border border-white/10"
              >
                Manage Users
              </button>
            )}

            {user ? (
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="hidden sm:inline text-sm text-neutral-400 max-w-[14rem] truncate">{user.email}</span>
                <button
                  onClick={onLogout}
                  className="px-3 py-2 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg transition-colors text-sm border border-white/10"
                >
                  Logout
                </button>
              </div>
            ) : (
              <button
                onClick={onLogin}
                className="px-3 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors text-sm font-medium shadow-lg shadow-green-600/20"
              >
                Login with Google
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

const ServiceGrid = ({ services, isAdmin, userPermissions, userServiceIds, onAddIncident, onSetStatus, onRestart, serviceUptimes, uptimeWindow }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-12 md:mb-16">
    {services.map((service, index) => (
      <ServiceCard key={service.id} service={service} isAdmin={isAdmin} userPermissions={userPermissions} userServiceIds={userServiceIds} onAddIncident={onAddIncident} onSetStatus={onSetStatus} onRestart={onRestart} uptime={serviceUptimes[service.id]} uptimeWindow={uptimeWindow} animationDelay={index * 80} />
    ))}
  </div>
);

const ServiceCard = ({ service, isAdmin, userPermissions, userServiceIds, onAddIncident, onSetStatus, onRestart, uptime, uptimeWindow, animationDelay }) => {
    const { id, name, status, responseTime, icon, canRestart } = service;
    const style = STATUS_STYLES[status] || STATUS_STYLES.UNKNOWN;
    const [latencyData, setLatencyData] = useState([]);
    const [menuOpen, setMenuOpen] = useState(false);

    const hasPermission = isAdmin || userPermissions.includes(id) || userServiceIds.includes(id);
    
    useEffect(() => {
        const q = query(collection(db, `status/${id}/latency`), orderBy('timestamp', 'desc'), limit(360));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ time: doc.data().timestamp?.toDate().getTime(), value: doc.data().value })).reverse();
            setLatencyData(data);
        });
        return () => unsubscribe();
    }, [id]);

    return (
        <div className={`bg-neutral-900/60 backdrop-blur-md border rounded-2xl flex flex-col p-5 shadow-card hover:-translate-y-0.5 hover:bg-neutral-900/80 transition-all duration-300 animate-fade-in-up ${style.borderColor}`} style={{ animationDelay: `${animationDelay}ms` }}>
            <div className="flex justify-between items-start mb-4 gap-3">
                 <div className="flex items-center space-x-3 min-w-0">
                    <div className={`p-2.5 rounded-xl ${style.bgColor}`}><div className={style.color}>{icon}</div></div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-white text-[0.95rem] leading-snug">{name}</h3>
                      <span className={`mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${style.bgColor} ${style.color}`}>
                        <span className={`h-1.5 w-1.5 rounded-full bg-current ${status === 'OPERATIONAL' ? 'animate-pulse' : ''}`}></span>
                        {statusLabel(status, isAdmin)}
                      </span>
                    </div>
                </div>
                <div className="relative">
                    {hasPermission && <button onClick={() => setMenuOpen(!menuOpen)} className="p-1 text-neutral-500 hover:text-white rounded-full"><MoreVertical size={20}/></button>}
                    {menuOpen && (
                        <div className="absolute top-8 right-0 bg-neutral-800 rounded-lg shadow-2xl w-48 z-20 border border-neutral-700 text-sm">
                            <button onClick={() => { onRestart(service); setMenuOpen(false); }} className="w-full text-left flex items-center space-x-2 px-3 py-2 hover:bg-neutral-700 disabled:opacity-50" disabled={!canRestart}><Zap size={14}/><span>Restart</span></button>
                            <button onClick={() => { onSetStatus(id, 'MAINTENANCE'); setMenuOpen(false); }} className="w-full text-left flex items-center space-x-2 px-3 py-2 hover:bg-neutral-700"><Wrench size={14}/><span>Maintenance</span></button>
                            <button onClick={() => { onSetStatus(id, 'DISABLED'); setMenuOpen(false); }} className="w-full text-left flex items-center space-x-2 px-3 py-2 hover:bg-neutral-700"><ShieldOff size={14}/><span>Disable</span></button>
                            <button onClick={() => { onSetStatus(id, 'OPERATIONAL'); setMenuOpen(false); }} className="w-full text-left flex items-center space-x-2 px-3 py-2 hover:bg-neutral-700"><CheckCircle size={14}/><span>Set Operational</span></button>
                            <div className="h-px bg-neutral-700 my-1"></div>
                            <button onClick={() => { onAddIncident(service); setMenuOpen(false); }} className="w-full text-left flex items-center space-x-2 px-3 py-2 hover:bg-neutral-700"><MessageSquare size={14}/><span>Log Incident</span></button>
                        </div>
                    )}
                </div>
            </div>
            
            <div className="h-20 mb-4 -ml-5 -mr-2">
                <ResponsiveContainer width="100%" height="100%"><AreaChart data={latencyData}><defs><linearGradient id={`color-${id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={style.stroke} stopOpacity={0.4}/><stop offset="95%" stopColor={style.stroke} stopOpacity={0}/></linearGradient></defs><Tooltip contentStyle={{ backgroundColor: 'rgba(23, 23, 23, 0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem' }} labelFormatter={(label) => new Date(label).toLocaleTimeString()} formatter={(value) => [`${value} ms`, 'Response']}/><Area type="monotone" dataKey="value" stroke={style.stroke} strokeWidth={2} fillOpacity={1} fill={`url(#color-${id})`} /><YAxis hide={true} domain={[0, 'dataMax + 100']} /><XAxis dataKey="time" hide={true} /></AreaChart></ResponsiveContainer>
            </div>

            <div className="space-y-3 text-sm flex-grow mb-2">
                {isAdmin && (
                <div className="flex justify-between items-center text-neutral-400">
                    <span className="flex items-center"><Clock size={14} className="mr-2 opacity-50"/>Response Time</span>
                    <span className="font-medium text-neutral-200 tabular-nums">{responseTime !== null ? `${responseTime}ms` : 'N/A'}</span>
                </div>
                )}
                <div className="flex justify-between items-center text-neutral-400">
                    <span className="flex items-center"><TrendingUp size={14} className="mr-2 opacity-50"/>{windowPhrase((UPTIME_WINDOWS.find((w) => w.id === uptimeWindow) || UPTIME_WINDOWS[3]).id)} uptime</span>
                    <span className="font-medium text-neutral-200 tabular-nums">{uptime == null ? '…' : formatUptimePct(uptime)}</span>
                </div>
            </div>

            <div className="border-t border-white/10 mt-4 pt-4 flex items-center justify-between text-sm">
                 <a href={service.link} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-neutral-400 hover:text-white transition-colors"><LinkIcon size={14} /><span>Visit</span></a>
                 <span className={`font-medium ${style.color}`}>{statusLabel(status, isAdmin)}</span>
            </div>
        </div>
    );
};

const Notification = ({ message, type, onDismiss }) => (
    <div className={`fixed bottom-5 right-5 flex items-center space-x-4 p-4 rounded-xl shadow-2xl animate-fade-in-up border backdrop-blur-md ${type === 'error' ? 'bg-red-500/20 border-red-500/30 text-red-300' : 'bg-green-500/20 border-green-500/30 text-green-300'}`}>
        {type === 'error' ? <XCircle/> : <CheckCircle/>}
        <span>{message}</span>
        <button onClick={onDismiss} className="absolute top-1 right-2 text-neutral-500 hover:text-white">&times;</button>
    </div>
);

const HistoryLog = ({ history, incidents = [], isAdmin, userPermissions, userServiceIds, onDeleteIncident }) => {
    const [isShiftPressed, setIsShiftPressed] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e) => e.key === 'Shift' && setIsShiftPressed(true);
        const handleKeyUp = (e) => e.key === 'Shift' && setIsShiftPressed(false);
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    const grouped = useMemo(() => {
      const byDay = {};
      const add = (dateString, item) => {
        if (!dateString) return;
        if (!byDay[dateString]) byDay[dateString] = [];
        byDay[dateString].push(item);
      };
      (mergeSplitOutages(incidents || [])).forEach((inc) => {
        const startMs = toMillis(inc.startedAt);
        if (startMs == null) return;
        const endMs = toMillis(inc.endedAt);
        const dateString = new Date(startMs).toISOString().split('T')[0];
        add(dateString, {
          kind: 'incident',
          id: inc.id,
          serviceId: inc.serviceId,
          serviceName: inc.serviceName,
          status: inc.severity || inc.status,
          startedAt: startMs,
          endedAt: endMs,
          open: inc.open !== false && endMs == null,
          sort: startMs,
        });
      });
      Object.keys(history || {}).forEach((dateString) => {
        (history[dateString]?.events || []).forEach((event) => {
          if (event.status !== 'UPDATE') return;
          const sort = event.timestamp?.seconds ? event.timestamp.seconds * 1000 : 0;
          add(dateString, { kind: 'update', dateString, ...event, sort });
        });
      });
      Object.keys(byDay).forEach((day) => {
        byDay[day].sort((a, b) => (b.sort || 0) - (a.sort || 0));
      });
      return byDay;
    }, [history, incidents]);

    const sortedHistoryKeys = useMemo(
      () => Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a)),
      [grouped]
    );
    
    return (
      <div className="animate-fade-in-up" style={{animationDelay: `${servicesConfig.length * 80}ms`}}>
        <div className="mb-6">
          <h2 className="text-xl md:text-2xl font-semibold text-white tracking-tight">Incident History</h2>
          {isAdmin && (
            <p className="text-sm text-neutral-500 mt-1">Outages use real start and end times. Markdown is rendered in these posts.</p>
          )}
        </div>
        <div className="space-y-8">
          {sortedHistoryKeys.length === 0 && (
            <p className="text-sm text-neutral-500">No recent incidents.</p>
          )}
          {sortedHistoryKeys.map(dateString => {
            const dayItems = grouped[dateString] || [];
            if (!dayItems.length) return null;
            return (
              <div key={dateString}>
                <h3 className="font-medium text-neutral-300 mb-4 pb-2 border-b border-white/10">{new Date(dateString + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</h3>
                <ul className="space-y-3">
                  {dayItems.map(event => {
                    const style = STATUS_STYLES[event.status] || STATUS_STYLES.UNKNOWN;
                    const hasPermission = isAdmin || userPermissions.includes(event.serviceId) || userServiceIds.includes(event.serviceId);
                    const durationMs = event.kind === 'incident'
                      ? (event.open ? Date.now() : (event.endedAt || event.startedAt)) - event.startedAt
                      : null;
                    return (
                      <li key={`${event.kind}-${event.id}`} className={`rounded-xl border ${style.borderColor} bg-neutral-900/50 px-4 py-3.5`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start space-x-3 min-w-0">
                              <div className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${style.color.replace('text-', 'bg-')}`}></div>
                              <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                    <span className="font-semibold text-white">{event.serviceName}</span>
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${style.bgColor} ${style.color}`}>{statusLabel(event.status, isAdmin)}</span>
                                    {event.kind === 'incident' && event.open && (
                                      <span className="text-xs text-neutral-500">Ongoing</span>
                                    )}
                                  </div>
                                  {event.kind === 'incident' && (
                                    <p className="text-neutral-400 mt-2 text-sm">
                                      Started {new Date(event.startedAt).toLocaleString()}
                                      {event.open
                                        ? ' · Ongoing'
                                        : ` · Ended ${new Date(event.endedAt).toLocaleString()}`}
                                      {durationMs != null ? ` (${formatDuration(durationMs)})` : ''}
                                    </p>
                                  )}
                                  {event.message && (
                                    <StatusMarkdown className="text-neutral-400 mt-2">{event.message}</StatusMarkdown>
                                  )}
                                  {event.adminNote && (
                                    <div className="mt-2 rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2">
                                      <StatusMarkdown className="text-neutral-300">{event.adminNote}</StatusMarkdown>
                                    </div>
                                  )}
                              </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                              {event.kind !== 'incident' && (
                                <span className="text-neutral-500 text-xs tabular-nums">({new Date(event.timestamp?.seconds * 1000).toLocaleTimeString()})</span>
                              )}
                              {event.kind === 'update' && hasPermission && (
                                  <button 
                                      onClick={() => onDeleteIncident(event.dateString || dateString, event.id, isShiftPressed)} 
                                      className={`transition-colors ${isShiftPressed ? 'text-red-500' : 'text-neutral-500 hover:text-red-500'}`}
                                  >
                                      <Trash2 size={14}/>
                                  </button>
                              )}
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    );
};

const IncidentModal = ({ service, onClose }) => {
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setIsSubmitting(true);
    try {
      const dateString = new Date().toISOString().split('T')[0];
      await setDoc(doc(collection(db, `history/${dateString}/events`)), {
        serviceId: service.id, serviceName: service.name, status: 'UPDATE', timestamp: new Date(),
        message: `An update was posted for ${service.name}.`, adminNote: note,
      });
      onClose();
    } catch (error) { console.error("Error posting update:", error); } 
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex justify-center items-center z-50 animate-fade-in">
      <div className="bg-neutral-900/90 rounded-2xl p-6 sm:p-8 w-full max-w-lg m-4 border border-white/10 shadow-card">
        <h2 className="text-xl font-semibold mb-1 text-white">Log update for <span className="text-green-400">{service.name}</span></h2>
        <p className="text-sm text-neutral-400 mb-4">This note is shown on the public incident history.</p>
        <form onSubmit={handleSubmit}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={"**Investigating** latency on the API.\n\n- Impact: some requests are slow\n- Next: rolling a restart"}
            className="w-full h-36 p-3 bg-neutral-950/80 border border-neutral-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500/70"
            required
          />
          <p className="mt-2 text-xs text-neutral-500">
            Markdown supported: <code className="text-neutral-400">**bold**</code>,{' '}
            <code className="text-neutral-400">[links](https://)</code>, lists, <code className="text-neutral-400">`code`</code>, and line breaks.
          </p>
          <div className="flex justify-end space-x-3 mt-6">
            <button type="button" onClick={onClose} className="py-2 px-4 bg-neutral-800 hover:bg-neutral-700 rounded-lg transition-colors border border-white/10">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="py-2 px-4 bg-green-600 hover:bg-green-500 rounded-lg transition-colors disabled:opacity-50 font-medium">Log Incident</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserManagementModal = ({ isOpen, onClose, users, onCreateUser, onUpdateUser, onDeleteUser, onCleanupOldUser, servicesConfig }) => {
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPermissions, setNewUserPermissions] = useState([]);
  const [newUserServiceIds, setNewUserServiceIds] = useState([]);
  const [editingUser, setEditingUser] = useState(null);

  if (!isOpen) return null;

  const handleCreateUser = () => {
    if (!newUserEmail.trim()) return;
    onCreateUser(newUserEmail, newUserPermissions, newUserServiceIds);
    setNewUserEmail('');
    setNewUserPermissions([]);
    setNewUserServiceIds([]);
  };

  const handleUpdateUser = (user) => {
    onUpdateUser(user.email, user.permissions, user.serviceIds);
    setEditingUser(null);
  };

  const togglePermission = (permission, currentPermissions, setPermissions) => {
    if (currentPermissions.includes(permission)) {
      setPermissions(currentPermissions.filter(p => p !== permission));
    } else {
      setPermissions([...currentPermissions, permission]);
    }
  };

  const toggleServiceId = (serviceId, currentServiceIds, setServiceIds) => {
    if (currentServiceIds.includes(serviceId)) {
      setServiceIds(currentServiceIds.filter(id => id !== serviceId));
    } else {
      setServiceIds([...currentServiceIds, serviceId]);
    }
  };

  const toggleEditingPermission = (permission) => {
    const currentPermissions = editingUser.permissions || [];
    const newPermissions = currentPermissions.includes(permission) 
      ? currentPermissions.filter(p => p !== permission)
      : [...currentPermissions, permission];
    
    setEditingUser({
      ...editingUser,
      permissions: newPermissions
    });
  };

  const toggleEditingServiceId = (serviceId) => {
    const currentServiceIds = editingUser.serviceIds || [];
    const newServiceIds = currentServiceIds.includes(serviceId)
      ? currentServiceIds.filter(id => id !== serviceId)
      : [...currentServiceIds, serviceId];
    
    setEditingUser({
      ...editingUser,
      serviceIds: newServiceIds
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex justify-center items-center z-50 animate-fade-in">
      <div className="bg-neutral-900/80 rounded-xl p-8 w-full max-w-4xl m-4 border border-blue-500/30 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">User Management</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-white">
            <XCircle size={24} />
          </button>
        </div>

        {/* Create New User */}
        <div className="bg-neutral-800/50 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">Create New User</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">Email</label>
              <input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                className="w-full p-3 bg-neutral-900/70 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">Permissions</label>
              <div className="space-y-2">
                {['admin', 'moderator', 'viewer'].map(permission => (
                  <label key={permission} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={newUserPermissions.includes(permission)}
                      onChange={() => togglePermission(permission, newUserPermissions, setNewUserPermissions)}
                      className="rounded border-neutral-600 bg-neutral-700 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-sm text-neutral-300 capitalize">{permission}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-neutral-300 mb-2">Service Access</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {servicesConfig.map(service => (
                <label key={service.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={newUserServiceIds.includes(service.id)}
                    onChange={() => toggleServiceId(service.id, newUserServiceIds, setNewUserServiceIds)}
                    className="rounded border-neutral-600 bg-neutral-700 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-sm text-neutral-300">{service.name}</span>
                </label>
              ))}
            </div>
          </div>
          <button
            onClick={handleCreateUser}
            disabled={!newUserEmail.trim()}
            className="mt-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            Create User
          </button>
        </div>

        {/* Existing Users */}
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">Existing Users</h3>
          <div className="space-y-4">
            {users.map(user => (
              <div key={user.id} className="bg-neutral-800/50 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-medium text-white">{user.email}</h4>
                    <p className="text-sm text-neutral-400">
                      Created: {user.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    {editingUser?.id === user.id ? (
                      <>
                        <button
                          onClick={() => handleUpdateUser(editingUser)}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingUser(null)}
                          className="px-3 py-1 bg-neutral-600 hover:bg-neutral-500 text-white rounded text-sm"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditingUser(user)}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                        >
                          Edit
                        </button>
                        {user.email ? (
                          <button
                            onClick={() => onDeleteUser(user.email)}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                          >
                            Delete
                          </button>
                        ) : (
                          <button
                            onClick={() => onCleanupOldUser(user.id)}
                            className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white rounded text-sm"
                            title="Clean up old user document"
                          >
                            Cleanup
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                {editingUser?.id === user.id ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-300 mb-2">Permissions</label>
                      <div className="space-y-2">
                        {['admin', 'moderator', 'viewer'].map(permission => (
                          <label key={permission} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={editingUser.permissions.includes(permission)}
                              onChange={() => toggleEditingPermission(permission)}
                              className="rounded border-neutral-600 bg-neutral-700 text-blue-500 focus:ring-blue-500"
                            />
                            <span className="text-sm text-neutral-300 capitalize">{permission}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-300 mb-2">Service Access</label>
                      <div className="grid grid-cols-2 gap-2">
                        {servicesConfig.map(service => (
                          <label key={service.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={editingUser.serviceIds.includes(service.id)}
                              onChange={() => toggleEditingServiceId(service.id)}
                              className="rounded border-neutral-600 bg-neutral-700 text-blue-500 focus:ring-blue-500"
                            />
                            <span className="text-sm text-neutral-300">{service.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-neutral-300">
                        <span className="font-medium">Email:</span> {user.email || 'Missing (Old User)'}
                      </p>
                      <p className="text-sm text-neutral-300">
                        <span className="font-medium">Permissions:</span> {user.permissions?.length > 0 ? user.permissions.join(', ') : 'None'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-300">
                        <span className="font-medium">Service Access:</span> {user.serviceIds?.length > 0 ? user.serviceIds.join(', ') : 'None'}
                      </p>
                      <p className="text-sm text-neutral-300">
                        <span className="font-medium">User ID:</span> {user.id}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Footer = () => (
    <footer className="text-center py-10 mt-4 text-neutral-600 text-sm border-t border-white/5">
        <p>Made with ❤️ by Frogmaster</p>
        <p className="mt-1 text-neutral-700">status.frogmaster.xyz</p>
    </footer>
);
